// File: src/pages/admin/ReturnProcessManagement.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  TextField,
  Avatar,
  Tabs,
  Tab,
  Pagination,
  InputAdornment,
  Stack,
  Divider,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Collapse,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Edit,
  Refresh,
  Visibility,
  Person,
  AttachMoney,
  Comment,
  DeleteSweep,
  ArrowBack,
  Search,
  QrCode2,
  Download,
  Print,
  Close,
  Image as ImageIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Receipt,
  FileCopy,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Autorenew,
  AssignmentReturn,
  Info,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

// Types and Interfaces
export interface ReturnItem {
  id: number;
  return_id: number;
  order_id: number;
  order_type: 'regular' | 'guest';
  user_id: number | null;
  guest_user_id: number | null;
  product_id: number;
  order_item_id: number;
  return_qty: number;
  refund_mode: 'wallet' | 'bank_transfer' | 'cash';
  request_type: 'refund' | 'replacement';
  price: string;
  total: string;
  reason: string;
  notes: string | null;
  created_at: string;
  status: number; // ACTUAL STATUS FIELD FROM API
  product_name?: string;
  user_name?: string;
  user_email?: string;
  order_current_status?: number; // Actual order status
  order_status_text?: string; // Actual order status text
}

export interface ReturnApproveRequest {
  return_id: number;
  type: number; // 14, 15, 18, 19, 21, 22
}

export interface ReturnApproveResponse {
  success: boolean;
  status: number;
  status_text: string;
  message?: string;
}

export interface ReturnItemsResponse {
  success: boolean;
  message: string;
  total_records: number;
  data: ReturnItem[];
}

// Regular Order Status Options (0-22)
const ORDER_STATUS_OPTIONS = [
  { value: 0, label: 'Payment Pending', color: '#ff9800' },
  { value: 1, label: 'Payment Success', color: '#2196f3' },
  { value: 2, label: 'Processing', color: '#4caf50' },
  { value: 3, label: 'Pickup Pending', color: '#795548' },
  { value: 4, label: 'Ware House Pickup', color: '#f44336' },
  { value: 5, label: 'In Transit', color: '#511111' },
  { value: 6, label: 'Out for delivery', color: '#427489' },
  { value: 7, label: 'Delivered', color: '#7ca14d' },
  { value: 8, label: 'Not delivered', color: '#c474a0' },
  { value: 9, label: 'Cancelled by user', color: '#f4559a' },
  { value: 10, label: 'Return and replacement', color: '#c6b0ec' },
  { value: 11, label: 'Return and refund', color: '#2b061a' },
  { value: 12, label: 'Order cancelled by admin', color: '#96e8de' },
  { value: 13, label: 'Order Delayed', color: '#ff002b' },
  { value: 14, label: 'Return refund in progress', color: '#9c27b0' },
  { value: 15, label: 'Return completed refund initiated', color: '#3f51b5' },
  { value: 16, label: 'Partially delivered', color: '#ff9800' },
  { value: 17, label: 'Return', color: '#607d8b' },
  { value: 18, label: 'Return Replacement in Progress', color: '#FF6B35' },
  { value: 19, label: 'Return Completed replacement initiated', color: '#6A0572' },
  { value: 20, label: 'Payment failed', color: '#edc573' },
  { value: 21, label: 'Refund Completed', color: '#1A936F' },
  { value: 22, label: 'Replacement Completed', color: '#114B5F' },
];

// Return-specific statuses (these are the ones we manage in return process)
const RETURN_PROCESS_STATUSES = [14, 15, 18, 19, 21, 22];

// Admin can only change these 4 statuses in return process
const ADMIN_RETURN_UPDATEABLE_STATUSES = [15, 19, 21, 22];

// Statuses that are considered "completed" and should disable edit
const COMPLETED_STATUSES = [21, 22];

// Helper functions
const getStatusText = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.label : `Status ${status}`;
};

const getStatusColor = (status: number): string => {
  const option = ORDER_STATUS_OPTIONS.find(opt => opt.value === status);
  return option ? option.color : '#9e9e9e';
};

// Get return status text - use the actual status field from API
const getReturnStatusText = (item: ReturnItem): string => {
  // Use the actual status field from return_items table
  const returnStatus = item.status;
  return getStatusText(returnStatus);
};

// Get return status number - use the actual status field
const getReturnStatus = (item: ReturnItem): number => {
  return item.status;
};

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const formatCurrency = (amount: string | number) => {
  const n = Number(amount || 0);
  if (Number.isNaN(n)) return `AED ${amount}`;
  return `AED ${n.toFixed(2)}`;
};

// Tab Panel Component
function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`return-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// API Service Functions
const fetchReturnItems = async (): Promise<ReturnItemsResponse> => {
  const token = localStorage.getItem('admin_token');
  const formData = new URLSearchParams();
  
  console.log('📡 Fetching return items...');
  
  const response = await fetch('https://fftestservice.sarsetech.com/user/return-itemslist', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  console.log('📊 API Response for return items:', data);
  
  if (data.data && data.data.length > 0) {
    console.log('📋 First return item:', data.data[0]);
    console.log('🔍 Status field in API response:', data.data[0].status);
  }
  
  return data;
};

// Fetch actual order status for each return
const fetchOrderStatus = async (orderId: number, orderType: 'regular' | 'guest'): Promise<{status: number, status_text: string}> => {
  const token = localStorage.getItem('admin_token');
  const formData = new URLSearchParams();
  formData.append('order_id', orderId.toString());

  let url = '';
  if (orderType === 'regular') {
    url = 'https://fftestservice.sarsetech.com/user/getOrderStatus';
  } else {
    url = 'https://fftestservice.sarsetech.com/user/getGuestOrderStatus';
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    console.warn(`Failed to fetch order status for ${orderType} order ${orderId}`);
    return { status: 0, status_text: 'Unknown' };
  }

  const data = await response.json();
  console.log(`📊 Order status for ${orderType} order ${orderId}:`, data);
  return {
    status: data.status || 0,
    status_text: data.status_text || 'Unknown'
  };
};

const approveReturn = async (data: ReturnApproveRequest): Promise<ReturnApproveResponse> => {
  const token = localStorage.getItem('admin_token');
  const formData = new URLSearchParams();
  formData.append('return_id', data.return_id.toString());
  formData.append('type', data.type.toString());

  console.log('📡 Approving return:', data);

  const response = await fetch('https://fftestservice.sarsetech.com/user/adminreturnapprove', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const result = await response.json();
  console.log('📊 Return approval response:', result);
  return result;
};

const ReturnProcessManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<ReturnItem | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [returnItems, setReturnItems] = useState<ReturnItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<ReturnItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [requestTypeFilter, setRequestTypeFilter] = useState<string[]>([]);
  const [refundModeFilter, setRefundModeFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // Load return items
  useEffect(() => {
    loadReturnItems();
  }, []);

  const loadReturnItems = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchReturnItems();
      if (response.success) {
        console.log('✅ Return items loaded successfully:', response.data.length);
        
        // Log all status values to verify
        response.data.forEach((item, index) => {
          console.log(`📊 Item ${index + 1}: Return ID ${item.return_id}, Status: ${item.status}, Status Text: ${getStatusText(item.status)}`);
        });
        
        // Fetch order status for each return item
        const itemsWithOrderStatus = await Promise.all(
          response.data.map(async (item) => {
            try {
              const orderStatus = await fetchOrderStatus(item.order_id, item.order_type);
              
              console.log('📋 Return item data:', {
                id: item.id,
                return_id: item.return_id,
                status: item.status, // This is the actual status from return_items table
                status_text: getStatusText(item.status),
                order_status: orderStatus.status,
                order_status_text: orderStatus.status_text
              });
              
              return {
                ...item,
                order_current_status: orderStatus.status,
                order_status_text: orderStatus.status_text
              };
            } catch (err) {
              console.warn(`Failed to fetch status for order ${item.order_id}:`, err);
              return {
                ...item,
                order_current_status: 0,
                order_status_text: 'Unknown'
              };
            }
          })
        );
        
        setReturnItems(itemsWithOrderStatus);
        setFilteredItems(itemsWithOrderStatus);
      } else {
        setError(response.message || 'Failed to load return items');
      }
    } catch (err: any) {
      console.error('❌ Error loading return items:', err);
      setError(err.message || 'Network error');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let result = returnItems;

    // Search filter
    if (searchStr) {
      const search = searchStr.toLowerCase();
      result = result.filter(item =>
        item.id.toString().includes(search) ||
        item.order_id.toString().includes(search) ||
        item.return_id.toString().includes(search) ||
        (item.product_name && item.product_name.toLowerCase().includes(search)) ||
        item.reason.toLowerCase().includes(search)
      );
    }

    // Status filter (filter by return process status, not order status)
    if (statusFilter.length > 0) {
      result = result.filter(item => statusFilter.includes(item.status));
    }

    // Request type filter
    if (requestTypeFilter.length > 0) {
      result = result.filter(item => requestTypeFilter.includes(item.request_type));
    }

    // Refund mode filter
    if (refundModeFilter.length > 0) {
      result = result.filter(item => refundModeFilter.includes(item.refund_mode));
    }

    setFilteredItems(result);
    setPage(1);
  }, [searchStr, statusFilter, requestTypeFilter, refundModeFilter, returnItems]);

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / rowsPerPage);
  const paginatedItems = filteredItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
  };

  const handleStatusFilterChange = (event: any) => {
    const value = event.target.value;
    setStatusFilter(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  const handleRequestTypeFilterChange = (event: any) => {
    const value = event.target.value;
    setRequestTypeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleRefundModeFilterChange = (event: any) => {
    const value = event.target.value;
    setRefundModeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClearFilters = () => {
    setSearchStr('');
    setStatusFilter([]);
    setRequestTypeFilter([]);
    setRefundModeFilter([]);
  };

  const handleToggleRow = (itemId: number) => {
    setExpandedRows(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleViewDetails = (item: ReturnItem) => {
    setSelectedReturn(item);
    setDetailsDialogOpen(true);
  };

  const handleStatusUpdateClick = (item: ReturnItem) => {
    setSelectedReturn(item);
    setSelectedStatus(item.status);
    setStatusDialogOpen(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedReturn) return;

    if (!ADMIN_RETURN_UPDATEABLE_STATUSES.includes(selectedStatus)) {
      setSnackbar({
        open: true,
        message: 'You can only update to specific statuses (15, 19, 21, 22)',
        severity: 'error', 
      });
      return;
    }

    setIsUpdating(true);
    try {
      const response = await approveReturn({
        return_id: selectedReturn.return_id,
        type: selectedStatus,
      });

      if (response.success) {
        // Update local state
        const updatedItems = returnItems.map((item) => {
          if (item.return_id === selectedReturn.return_id) {
            // Update the status field to the new status
            return { 
              ...item, 
              status: selectedStatus, // Update the actual status field
              order_current_status: selectedStatus, // Also update order status
              order_status_text: response.status_text
            };
          }
          return item;
        });

        setReturnItems(updatedItems);

        setSnackbar({
          open: true,
          message: `Status updated to ${response.status_text}`,
          severity: 'success',
        });
        setStatusDialogOpen(false);
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to update status',
          severity: 'error',
        });
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Network error',
        severity: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRefresh = () => {
    loadReturnItems();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get status chip for order status
  const getOrderStatusChip = (status: number) => {
    const statusOption = ORDER_STATUS_OPTIONS.find((opt) => opt.value === status);
    const isReturnStatus = RETURN_PROCESS_STATUSES.includes(status);

    return (
      <Chip
        label={statusOption?.label || `Status ${status}`}
        size="small"
        sx={{
          backgroundColor: statusOption?.color || '#9e9e9e',
          color: 'white',
          fontWeight: 600,
          fontSize: '11px',
          minWidth: 'fit-content',
          border: isReturnStatus ? '1px solid white' : 'none',
        }}
      />
    );
  };

  // Get status chip for return process status
  const getReturnStatusChip = (item: ReturnItem) => {
    const returnStatus = item.status;
    const returnStatusText = getStatusText(returnStatus);
    const statusOption = ORDER_STATUS_OPTIONS.find((opt) => opt.value === returnStatus);
    const isReadOnly = ![15, 19, 21, 22].includes(returnStatus);
    const isUpdateable = ADMIN_RETURN_UPDATEABLE_STATUSES.includes(returnStatus);

    console.log('🎯 Return status chip for item:', {
      return_id: item.return_id,
      returnStatus,
      returnStatusText,
      statusOption,
      isReadOnly,
      isUpdateable
    });

    return (
      <Tooltip 
        title={isReadOnly ? 'System-generated status (admin cannot change)' : 'Click to update'}
        arrow
      >
        <Box 
          onClick={() => !shouldDisableEdit(item) && handleStatusUpdateClick(item)} 
          sx={{ 
            cursor: shouldDisableEdit(item) ? 'default' : 'pointer',
            display: 'inline-block'
          }}
        >
          <Chip
            label={returnStatusText}
            size="small"
            sx={{
              backgroundColor: statusOption?.color || '#9e9e9e',
              color: 'white',
              fontWeight: 600,
              fontSize: '11px',
              minWidth: 'fit-content',
              border: isReadOnly ? '1px dashed white' : 'none',
              opacity: isReadOnly ? 0.8 : 1,
              cursor: shouldDisableEdit(item) ? 'default' : (isUpdateable ? 'pointer' : 'default'),
              '&:hover': !shouldDisableEdit(item) && isUpdateable ? { opacity: 0.9 } : {},
            }}
            icon={isReadOnly ? <Warning fontSize="small" /> : undefined}
          />

        </Box>
      </Tooltip>
    );
  };

  // Helper to check if edit should be disabled
  const shouldDisableEdit = (item: ReturnItem): boolean => {
    // Only disable edit if status is 21 or 22 (completed)
    return COMPLETED_STATUSES.includes(item.status);
  };

  // Filter options for return statuses only
  const RETURN_STATUS_FILTER_OPTIONS = ORDER_STATUS_OPTIONS.filter(opt => 
    RETURN_PROCESS_STATUSES.includes(opt.value)
  );

  const REQUEST_TYPE_OPTIONS = [
    { value: 'refund', label: 'Refund' },
    { value: 'replacement', label: 'Replacement' },
  ];

  const REFUND_MODE_OPTIONS = [
    { value: 'wallet', label: 'Wallet' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4', p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Return Process Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton onClick={handleRefresh} disabled={isLoading} title="Refresh">
                    <Refresh />
                  </IconButton>
                  <Button
                    variant="outlined"
                    startIcon={<AssignmentReturn />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>

              {/* Filters Card */}
              <Card sx={{ mb: 3, p: 2, borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search returns..."
                    value={searchStr}
                    onChange={handleSearch}
                    sx={{ flex: 1, minWidth: 200 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Status Filter */}
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Return Status</InputLabel>
                    <Select
                      multiple
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      input={<OutlinedInput label="Return Status" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const option = RETURN_STATUS_FILTER_OPTIONS.find(opt => opt.value === value);
                            return (
                              <Chip
                                key={value}
                                label={option?.label || value}
                                size="small"
                                sx={{
                                  backgroundColor: option?.color || '#9e9e9e',
                                  color: 'white'
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {RETURN_STATUS_FILTER_OPTIONS.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          <Checkbox checked={statusFilter.indexOf(status.value) > -1} />
                          <ListItemText primary={status.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Request Type Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Request Type</InputLabel>
                    <Select
                      multiple
                      value={requestTypeFilter}
                      onChange={handleRequestTypeFilterChange}
                      input={<OutlinedInput label="Request Type" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {REQUEST_TYPE_OPTIONS.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Checkbox checked={requestTypeFilter.indexOf(type.value) > -1} />
                          <ListItemText primary={type.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Refund Mode Filter */}
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Refund Mode</InputLabel>
                    <Select
                      multiple
                      value={refundModeFilter}
                      onChange={handleRefundModeFilterChange}
                      input={<OutlinedInput label="Refund Mode" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {REFUND_MODE_OPTIONS.map((mode) => (
                        <MenuItem key={mode.value} value={mode.value}>
                          <Checkbox checked={refundModeFilter.indexOf(mode.value) > -1} />
                          <ListItemText primary={mode.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Clear Filters Button */}
                  {(searchStr || statusFilter.length > 0 || requestTypeFilter.length > 0 || refundModeFilter.length > 0) && (
                    <Button variant="outlined" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                  )}
                </Box>
              </Card>

              {/* Main Content Card */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                {/* Tabs */}
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab label={`All Returns (${returnItems.length})`} />
                  </Tabs>
                </Box>

                {/* Loading State */}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                    <CircularProgress />
                  </Box>
                )}

                {/* Error State */}
                {error && !isLoading && (
                  <Box sx={{ p: 3 }}>
                    <Alert severity="error" action={
                      <Button color="inherit" size="small" onClick={handleRefresh}>
                        Retry
                      </Button>
                    }>
                      {error}
                    </Alert>
                  </Box>
                )}

                {/* Empty State */}
                {!isLoading && !error && filteredItems.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No return items found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchStr || statusFilter.length > 0 || requestTypeFilter.length > 0 || refundModeFilter.length > 0
                        ? 'No results matching your filters'
                        : 'Return requests will appear here'}
                    </Typography>
                    <Button variant="outlined" onClick={handleClearFilters}>
                      Clear Filters
                    </Button>
                  </Box>
                )}

                {/* Desktop Table View */}
                {!isLoading && !error && filteredItems.length > 0 && (
                  <>
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#fafbfb' }}>
                              <TableCell><Typography variant="caption" fontWeight={600}>Return ID</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Order ID</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Product</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Type</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Return Status</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Amount</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Created</Typography></TableCell>
                              <TableCell><Typography variant="caption" fontWeight={600}>Actions</Typography></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paginatedItems.map((item) => {
                              console.log('📋 Rendering table row for return:', {
                                return_id: item.return_id,
                                status: item.status,
                                status_text: getStatusText(item.status)
                              });
                              
                              return (
                                <TableRow key={item.id} hover>
                                  <TableCell>#{item.return_id}</TableCell>
                                  <TableCell>#{item.order_id}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2">{item.product_name || `Product ${item.product_id}`}</Typography>
                                    <Typography variant="caption" color="text.secondary">Qty: {item.return_qty}</Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={item.request_type === 'refund' ? 'Refund' : 'Replacement'}
                                      size="small"
                                      color={item.request_type === 'refund' ? 'primary' : 'secondary'}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      {item.refund_mode}
                                    </Typography>
                                  </TableCell>
                                  
                                  <TableCell>
                                    {getReturnStatusChip(item)}
                                  </TableCell>
                                  
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatCurrency(item.total)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>{formatDate(item.created_at)}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleViewDetails(item)}
                                        title="View Details"
                                      >
                                        <Visibility fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleStatusUpdateClick(item)}
                                        title="Update Return Status"
                                        disabled={shouldDisableEdit(item)}
                                      >
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Mobile Card View */}
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      {paginatedItems.map((item) => (
                        <Card key={item.id} sx={{ m: 2, p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6">Return #{item.return_id}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Order #{item.order_id} • {formatDate(item.created_at)}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => handleToggleRow(item.id)}>
                              {expandedRows.includes(item.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </Box>

                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Product</Typography>
                              <Typography variant="body2">{item.product_name || `Product ${item.product_id}`}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Quantity</Typography>
                              <Typography variant="body2">{item.return_qty}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Type</Typography>
                              <Chip
                                label={item.request_type === 'refund' ? 'Refund' : 'Replacement'}
                                size="small"
                                color={item.request_type === 'refund' ? 'primary' : 'secondary'}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Amount</Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(item.total)}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Typography variant="caption" color="text.secondary">Return Status</Typography>
                              <Box sx={{ mt: 0.5 }}>
                                {getReturnStatusChip(item)}
                              </Box>
                            </Grid>
                          </Grid>

                          <Collapse in={expandedRows.includes(item.id)}>
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e1e3e5' }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Reason
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {item.reason}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={() => handleViewDetails(item)}
                                  fullWidth
                                >
                                  View Details
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Edit />}
                                  onClick={() => handleStatusUpdateClick(item)}
                                  disabled={shouldDisableEdit(item)}
                                  fullWidth
                                >
                                  Update Status
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>

                          {!expandedRows.includes(item.id) && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleViewDetails(item)}
                                fullWidth
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleStatusUpdateClick(item)}
                                disabled={shouldDisableEdit(item)}
                                fullWidth
                              >
                                Update
                              </Button>
                            </Box>
                          )}
                        </Card>
                      ))}
                    </Box>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Pagination
                          count={totalPages}
                          page={page}
                          onChange={(e, newPage) => setPage(newPage)}
                          color="primary"
                        />
                      </Box>
                    )}
                  </>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Return Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Update return status for Return #{selectedReturn?.return_id} (Order #{selectedReturn?.order_id})
          </Typography>
          
          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Current Order Status:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {selectedReturn?.order_status_text || 'Unknown'} (Status: {selectedReturn?.order_current_status || 0})
            </Typography>
          </Box>

          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Current Return Status:
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {selectedReturn ? getStatusText(selectedReturn.status) : 'Unknown'} 
              (Status: {selectedReturn?.status || 0})
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Return Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(Number(e.target.value))}
              label="Select Return Status"
            >
              {RETURN_STATUS_FILTER_OPTIONS.map((status) => (
                <MenuItem 
                  key={status.value} 
                  value={status.value}
                  disabled={![15, 19, 21, 22].includes(status.value)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: status.color,
                      }}
                    />
                    {status.label} ({status.value})
                    {![15, 19, 21, 22].includes(status.value) && ' (System-generated)'}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Admin can only update status to:
              <ul>
                <li>15 - Return completed refund initiated</li>
                <li>19 - Return Completed replacement initiated</li>
                <li>21 - Refund Completed</li>
                <li>22 - Replacement Completed</li>
              </ul>
              Statuses 14 and 18 are system-generated and will update automatically.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={isUpdating || !ADMIN_RETURN_UPDATEABLE_STATUSES.includes(selectedStatus)}
            startIcon={isUpdating ? <CircularProgress size={20} /> : null}
          >
            {isUpdating ? 'Updating...' : 'Update Return Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          Return Details - #{selectedReturn?.return_id}
          <IconButton
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedReturn && (
            <Grid container spacing={3}>
              {/* Status Summary */}
              <Grid item xs={12}>
                <Card sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Return Process Status
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {getReturnStatusChip(selectedReturn)}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {getStatusText(selectedReturn.status)} (Status: {selectedReturn.status})
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Order Status
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        {getOrderStatusChip(selectedReturn.order_current_status || 0)}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedReturn.order_status_text || 'Unknown'} (Status: {selectedReturn.order_current_status || 0})
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Basic Info */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Return ID</Typography>
                      <Typography variant="body2">#{selectedReturn.return_id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Order ID</Typography>
                      <Typography variant="body2">#{selectedReturn.order_id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Order Type</Typography>
                      <Chip
                        label={selectedReturn.order_type}
                        size="small"
                        color={selectedReturn.order_type === 'regular' ? 'primary' : 'secondary'}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="body2">{formatDateTime(selectedReturn.created_at)}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">User ID</Typography>
                      <Typography variant="body2">
                        {selectedReturn.user_id || selectedReturn.guest_user_id || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Request Details */}
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Request Details
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Request Type</Typography>
                      <Chip
                        label={selectedReturn.request_type === 'refund' ? 'Refund' : 'Replacement'}
                        size="small"
                        color={selectedReturn.request_type === 'refund' ? 'primary' : 'secondary'}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Refund Mode</Typography>
                      <Typography variant="body2">{selectedReturn.refund_mode}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Product</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {selectedReturn.product_name || `Product ${selectedReturn.product_id}`}
                      </Typography>
                      <Typography variant="body2">ID: {selectedReturn.product_id}</Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Product & Financial Details */}
              <Grid item xs={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Product & Financial Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Product Details</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Product ID:</strong> {selectedReturn.product_id}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Order Item ID:</strong> {selectedReturn.order_item_id}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Return Quantity:</strong> {selectedReturn.return_qty}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Financial Details</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Unit Price:</Typography>
                          <Typography variant="body2">{formatCurrency(selectedReturn.price)}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Quantity:</Typography>
                          <Typography variant="body2">{selectedReturn.return_qty}</Typography>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" fontWeight={600}>Total Amount:</Typography>
                          <Typography variant="h6" color="primary">
                            {formatCurrency(selectedReturn.total)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>

              {/* Reason & Notes */}
              <Grid item xs={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Reason & Notes
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">Reason for Return</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      {selectedReturn.reason}
                    </Typography>
                  </Box>
                  {selectedReturn.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Additional Notes</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        {selectedReturn.notes}
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Card sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Actions
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => {
                        setDetailsDialogOpen(false);
                        handleStatusUpdateClick(selectedReturn);
                      }}
                      disabled={shouldDisableEdit(selectedReturn)}
                    >
                      Update Return Status
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleRefresh}
                      startIcon={<Refresh />}
                    >
                      Refresh Data
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReturnProcessManagement;